import React, { useEffect, useMemo, useState } from 'react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import {
  Alert, Box, Card, CardContent, CircularProgress, Grid, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { AdminOverviewData, fetchAdminOverview } from '../api/adminOverview';
import PageHeader from '../components/PageHeader';
import { ApiError } from '../utils/apiClient';

const displayDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '—';

const EmptyRow = ({ columns }: { columns: number }) => (
  <TableRow><TableCell colSpan={columns} align="center">No activity recorded yet.</TableCell></TableRow>
);

export default function AdminOverviewPage() {
  const [data, setData] = useState<AdminOverviewData | null>(null);
  const [error, setError] = useState<{ forbidden: boolean; message: string } | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    void fetchAdminOverview()
      .then(setData)
      .catch((cause: unknown) => {
        const forbidden = cause instanceof ApiError && cause.status === 403;
        setError({
          forbidden,
          message: forbidden
            ? 'Access denied. This page is restricted to allowlisted platform administrators.'
            : cause instanceof Error ? cause.message : 'Unable to load the admin overview.',
        });
      });
  }, []);

  const users = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return data?.latest_registered_users || [];
    return (data?.latest_registered_users || []).filter((user) =>
      [user.email, user.name, user.company, user.inferred_company_domain]
        .some((value) => value?.toLowerCase().includes(query))
    );
  }, [data, search]);

  const userColumns: GridColDef[] = [
    { field: 'email', headerName: 'Email', minWidth: 220, flex: 1 },
    { field: 'name', headerName: 'Name', minWidth: 150, flex: 0.7, valueFormatter: ({ value }) => value || '—' },
    { field: 'company', headerName: 'Company', minWidth: 150, flex: 0.7, valueFormatter: ({ value }) => value || '—' },
    { field: 'inferred_company_domain', headerName: 'Domain', minWidth: 150, flex: 0.7, valueFormatter: ({ value }) => value || '—' },
    { field: 'registration_date', headerName: 'Registered', width: 180, valueFormatter: ({ value }) => displayDate(value) },
    { field: 'last_activity_date', headerName: 'Last activity', width: 180, valueFormatter: ({ value }) => displayDate(value) },
    { field: 'number_of_projects', headerName: 'Projects', width: 90, type: 'number' },
    { field: 'number_of_runs', headerName: 'Runs', width: 80, type: 'number' },
    { field: 'cli_downloaded', headerName: 'CLI', width: 80, valueFormatter: ({ value }) => value ? 'Yes' : 'No' },
    { field: 'api_used', headerName: 'API', width: 80, valueFormatter: ({ value }) => value ? 'Yes' : 'No' },
  ];

  if (!data && !error) {
    return <Stack alignItems="center" sx={{ py: 12 }} spacing={2}><CircularProgress /><Typography>Loading admin overview…</Typography></Stack>;
  }
  if (error) {
    return <Alert severity={error.forbidden ? 'warning' : 'error'}><strong>{error.forbidden ? '403 — Access denied' : 'Unable to load dashboard'}</strong><br />{error.message}</Alert>;
  }
  if (!data) return null;

  const kpis = [
    ['Registered Users', data.total_registered_users], ['New Users 7d', data.new_users_last_7_days],
    ['Projects', data.total_projects], ['Runs', data.total_runs], ['CLI Downloads', data.total_cli_downloads],
    ['API Calls', data.total_api_calls], ['Failed Runs 7d', data.failed_runs_last_7_days],
  ];

  return <Box>
    <PageHeader icon={<AdminPanelSettingsIcon />} title="Admin Overview" subtitle="Private product traction and usage signals" badge="ADMIN" />

    <Grid container spacing={2} sx={{ mb: 4 }}>
      {kpis.map(([label, value]) => <Grid item xs={12} sm={6} md={3} key={String(label)}>
        <Card><CardContent><Typography color="text.secondary" variant="body2">{label}</Typography><Typography variant="h4" fontWeight={700}>{value}</Typography></CardContent></Card>
      </Grid>)}
    </Grid>

    <Typography variant="h5" gutterBottom>Users</Typography>
    <Paper sx={{ p: 2, mb: 4 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
      <Box><Typography color="text.secondary">New today</Typography><Typography variant="h5">{data.new_users_today}</Typography></Box>
      <Box><Typography color="text.secondary">New last 7 days</Typography><Typography variant="h5">{data.new_users_last_7_days}</Typography></Box>
    </Stack></Paper>

    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
      <Typography variant="h5">Registered Users</Typography>
      <TextField size="small" label="Search users" value={search} onChange={(event) => setSearch(event.target.value)} />
    </Stack>
    <Paper sx={{ height: 520, mb: 4, width: '100%' }}>
      <DataGrid rows={users.map((user, id) => ({ id, ...user }))} columns={userColumns} pageSize={10} rowsPerPageOptions={[10, 25, 50]} disableSelectionOnClick localeText={{ noRowsLabel: 'No registered users found.' }} />
    </Paper>

    <Typography variant="h5" gutterBottom>Company / Domain Signals</Typography>
    <TableContainer component={Paper} sx={{ mb: 4 }}><Table size="small"><TableHead><TableRow>
      {['Domain', 'Users', 'Active users', 'Projects', 'Runs', 'API calls 7d'].map((label) => <TableCell key={label}>{label}</TableCell>)}
    </TableRow></TableHead><TableBody>
      {!data.registered_users_by_company_domain.length ? <EmptyRow columns={6} /> : data.registered_users_by_company_domain.map((row) => <TableRow key={row.inferred_company_domain}>
        <TableCell>{row.inferred_company_domain}</TableCell><TableCell>{row.number_of_registered_users}</TableCell><TableCell>{row.number_of_active_users}</TableCell><TableCell>{row.total_projects}</TableCell><TableCell>{row.total_runs}</TableCell><TableCell>{row.api_calls_last_7_days}</TableCell>
      </TableRow>)}
    </TableBody></Table></TableContainer>

    <Grid container spacing={2} sx={{ mb: 4 }}>
      {[['CLI Downloads', data.total_cli_downloads, data.cli_downloads_last_7_days], ['API Usage', data.total_api_calls, data.api_calls_last_7_days], ['Runs', data.total_runs, data.runs_last_7_days]].map(([label, total, recent]) => <Grid item xs={12} md={4} key={String(label)}><Card><CardContent><Typography variant="h6">{label}</Typography><Typography variant="h4">{total}</Typography><Typography color="text.secondary">{recent} in the last 7 days</Typography></CardContent></Card></Grid>)}
    </Grid>

    <Typography variant="h5" gutterBottom>Latest Activity</Typography>
    <Grid container spacing={2}>
      {[['Latest signups', data.latest_signups], ['Latest runs', data.latest_runs], ['Latest CLI downloads', data.latest_cli_downloads], ['Latest API usage', data.latest_api_usage]].map(([label, rows]) => <Grid item xs={12} md={6} key={String(label)}><Paper sx={{ p: 2, height: '100%' }}><Typography variant="h6" gutterBottom>{label as string}</Typography>{!(rows as Record<string, unknown>[]).length ? <Typography color="text.secondary">No activity recorded yet.</Typography> : (rows as Record<string, unknown>[]).slice(0, 8).map((row, index) => <Box key={index} sx={{ py: 1, borderTop: index ? '1px solid rgba(255,255,255,.08)' : 0 }}><Typography variant="body2">{String(row.email || row.status || row.event_type || row.id || 'Activity')}</Typography><Typography variant="caption" color="text.secondary">{displayDate((row.created_at as string) || null)}</Typography></Box>)}</Paper></Grid>)}
    </Grid>
  </Box>;
}
